/**
 * Named saved schedules — localStorage-backed snapshots of event preferences.
 *
 * Storage key: `checkthelineup:schedules:v1`
 *
 * Shape:
 *   Schedule[] — flat array, indexed by id.
 *
 * Why flat: a user with schedules for multiple events sees them separated
 * by eventSlug in the UI, but the storage stays simple and migrating to
 * a remote store (if we ever add accounts) is a one-shot rewrite.
 */
import type { EventPrefs, Rating } from "./preferences";

const KEY = "checkthelineup:schedules:v1";

/** User-editable label cap — keeps the My Lineup list tidy. */
export const MAX_NAME_LENGTH = 80;

export type Schedule = {
  /** Unique opaque id. UUID-ish; generated client-side. */
  id: string;
  /** User-given label — trimmed, max 80 chars. */
  name: string;
  eventSlug: string;
  /** Epoch ms. */
  createdAt: number;
  /** Epoch ms — updated on overwrite/rename. */
  updatedAt: number;
  /** Snapshot of `{ artists, sets }` at save time. */
  picks: EventPrefs;
};

function readAll(): Schedule[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Tolerate partial rows — drop anything missing required fields.
    return parsed.filter(
      (s): s is Schedule =>
        !!s &&
        typeof s === "object" &&
        typeof s.id === "string" &&
        typeof s.name === "string" &&
        typeof s.eventSlug === "string" &&
        typeof s.createdAt === "number" &&
        typeof s.updatedAt === "number" &&
        !!s.picks &&
        typeof s.picks === "object",
    );
  } catch {
    return [];
  }
}

function writeAll(all: Schedule[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // Quota — fail silently.
  }
  broadcast();
}

function broadcast(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("schedules:changed"));
}

function genId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as { randomUUID(): string }).randomUUID();
  }
  return `s_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e9).toString(36)}`;
}

function sanitiseName(raw: string): string {
  return raw.trim().replace(/\s+/g, " ").slice(0, MAX_NAME_LENGTH);
}

/** List schedules for a given event, newest first. */
export function listSchedulesForEvent(eventSlug: string): Schedule[] {
  return readAll()
    .filter(s => s.eventSlug === eventSlug)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

/** List every saved schedule, newest first — used in a cross-event index. */
export function listAllSchedules(): Schedule[] {
  return readAll().sort((a, b) => b.updatedAt - a.updatedAt);
}

export function getSchedule(id: string): Schedule | null {
  return readAll().find(s => s.id === id) ?? null;
}

/** Save the given picks as a new named schedule. Returns the created row. */
export function saveSchedule(input: {
  name: string;
  eventSlug: string;
  picks: EventPrefs;
}): Schedule | null {
  const name = sanitiseName(input.name);
  if (!name) return null;
  const now = Date.now();
  const row: Schedule = {
    id: genId(),
    name,
    eventSlug: input.eventSlug,
    createdAt: now,
    updatedAt: now,
    picks: {
      artists: { ...(input.picks.artists ?? {}) },
      sets: { ...(input.picks.sets ?? {}) },
    },
  };
  const all = readAll();
  all.push(row);
  writeAll(all);
  return row;
}

/** Overwrite an existing schedule's picks (and optionally its name). */
export function updateSchedule(
  id: string,
  patch: { name?: string; picks?: EventPrefs },
): Schedule | null {
  const all = readAll();
  const idx = all.findIndex(s => s.id === id);
  if (idx < 0) return null;
  const existing = all[idx]!;
  const updated: Schedule = {
    ...existing,
    updatedAt: Date.now(),
  };
  if (patch.name !== undefined) {
    const clean = sanitiseName(patch.name);
    if (clean) updated.name = clean;
  }
  if (patch.picks !== undefined) {
    updated.picks = {
      artists: { ...(patch.picks.artists ?? {}) },
      sets: { ...(patch.picks.sets ?? {}) },
    };
  }
  all[idx] = updated;
  writeAll(all);
  return updated;
}

/** Delete a schedule. Returns true iff it existed. */
export function deleteSchedule(id: string): boolean {
  const all = readAll();
  const next = all.filter(s => s.id !== id);
  if (next.length === all.length) return false;
  writeAll(next);
  return true;
}

/**
 * Curator-mode helpers: mutate a named schedule's set-map without
 * touching the user's working preferences. Used by the "Add to…" picker
 * on the artist sheet.
 */

export function setContainsSet(id: string, setId: number): boolean {
  const s = getSchedule(id);
  return !!s && s.picks.sets[setId] != null;
}

/** Add (or upsert) a set into a schedule with `rating`. No-op if missing. */
export function addSetToSchedule(
  id: string,
  setId: number,
  rating: Rating = "like",
): Schedule | null {
  const s = getSchedule(id);
  if (!s) return null;
  return updateSchedule(id, {
    picks: {
      artists: { ...s.picks.artists },
      sets: { ...s.picks.sets, [setId]: rating },
    },
  });
}

/** Remove a set from a schedule's sets map. */
export function removeSetFromSchedule(id: string, setId: number): Schedule | null {
  const s = getSchedule(id);
  if (!s) return null;
  const sets = { ...s.picks.sets };
  delete sets[setId];
  return updateSchedule(id, {
    picks: { artists: { ...s.picks.artists }, sets },
  });
}

/** Which saved schedules (for the given event) currently include this set. */
export function schedulesContainingSet(eventSlug: string, setId: number): Schedule[] {
  return listSchedulesForEvent(eventSlug).filter(s => s.picks.sets[setId] != null);
}

/** Rough pick count of a schedule — effective likes + superlikes. */
export function countPicksInSchedule(schedule: Schedule): number {
  const artistRatings = Object.values(schedule.picks.artists ?? {}) as Rating[];
  const setRatings = Object.values(schedule.picks.sets ?? {}) as Rating[];
  let n = 0;
  for (const r of [...artistRatings, ...setRatings]) {
    if (r === "like" || r === "superlike") n++;
  }
  return n;
}
